// Ollaway motion. One idea: things tilt in, then settle level — like the liquid in the rings.
// Amplitudes stay small on purpose (this is a motion-sickness brand) and everything is skipped for reduced motion.
(() => {
  const root = document.documentElement;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const showAll = () => root.classList.remove('ol-js');
  if (reduced.matches || !('IntersectionObserver' in window)) { showAll(); return; }
  reduced.addEventListener('change', () => { if (reduced.matches) showAll(); });

  function splitWords(element, indexVar) {
    if (element.dataset.olSplit) return;
    element.dataset.olSplit = 'true';
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let index = 0;
    for (const node of nodes) {
      const fragment = document.createDocumentFragment();
      for (const part of node.textContent.split(/(\s+)/)) {
        if (!part.trim()) { fragment.append(part); continue; }
        const word = document.createElement('span');
        word.className = 'ollaway-word';
        word.textContent = part;
        word.style.setProperty(indexVar, index++);
        fragment.append(word);
      }
      node.replaceWith(fragment);
    }
    return index;
  }

  const inView = new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('ol-in');
      inView.unobserve(entry.target);
    }
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

  const skip = '.ollaway-chat, form, .ollaway-buy-box, .ol-hero, [data-ol-static], ollaway-sticky-buy, dialog, .olq, .olq-dialog, .ol-quiz-teaser, [class*="olq"]';
  const fills = [];
  const openers = [];
  const parallax = [];

  function scan(scope = document) {
    // Headings rise word by word and settle level.
    scope.querySelectorAll('.ollaway h2, .ollaway-footer-title').forEach(el => {
      if (el.classList.contains('ol-h') || el.closest(skip) || el.querySelector('a, button, input')) return;
      splitWords(el, '--w-i');
      el.classList.add('ol-h');
      inView.observe(el);
    });
    // Supporting copy, cards and media fade up; siblings inside a staggered grid get an index.
    scope.querySelectorAll('.ollaway .ollaway-kicker, .ollaway .ollaway-section-heading p, .ollaway-benefits__intro p:not(.ol-scrollfill), .ollaway-story>div:last-child, .ollaway-comparison>div:first-child>*:not(h2), .ollaway-table-wrap, .ol-faq__list, .ollaway-steps li, .ol-stagger>*, .ollaway-value-grid article, .ollaway-why-detail>div, .ol-closer__copy>*, .ol-use__copy li, .ol-use__media, .ollaway-hero--shop .ollaway-hero__media, .ollaway-hero--shop .ollaway-hero__copy>.rte, .ollaway-review-card, .ollaway-footer-nav').forEach(el => {
      if (el.classList.contains('ol-r') || el.closest(skip)) return;
      el.classList.add('ol-r');
      const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
      el.style.setProperty('--i', Math.max(siblings.indexOf(el), 0) % 4);
      inView.observe(el);
    });
    scope.querySelectorAll('.ollaway-compare').forEach(table => {
      if (table.dataset.olSeen) return;
      table.dataset.olSeen = 'true';
      table.querySelectorAll('tbody tr').forEach((row, i) => row.querySelectorAll('.ollaway-check').forEach(check => check.style.setProperty('--i', i)));
      inView.observe(table);
    });
    // The statement paragraph inks in with reading position.
    scope.querySelectorAll('.ollaway-benefits__intro p').forEach(el => {
      if (el.classList.contains('ol-scrollfill')) return;
      el.classList.remove('ol-r');
      el.classList.add('ol-scrollfill');
      splitWords(el, '--w-i');
      fills.push({ el, words: Array.from(el.querySelectorAll('.ollaway-word')) });
    });
    scope.querySelectorAll('.ollaway-evidence').forEach(el => { if (!openers.includes(el)) openers.push(el); });
    scope.querySelectorAll('[data-ol-parallax]').forEach(el => {
      if (parallax.some(item => item.el === el)) return;
      parallax.push({ el, target: el.querySelector('img') || el, rate: Number(el.dataset.olParallax) || 0.05 });
    });
    scope.querySelectorAll('.ol-faq details').forEach(accordion);
  }

  // Answers open and close with their real height instead of snapping.
  function accordion(details) {
    if (details.dataset.olBound) return;
    details.dataset.olBound = 'true';
    const summary = details.querySelector('summary');
    const body = details.querySelector('summary ~ *');
    if (!summary || !body) return;
    let animation;
    summary.addEventListener('click', event => {
      event.preventDefault();
      animation?.cancel();
      const opening = !details.open;
      if (opening) details.open = true;
      const height = body.scrollHeight;
      animation = body.animate(
        { height: opening ? ['0px', `${height}px`] : [`${height}px`, '0px'], opacity: opening ? [0, 1] : [1, 0] },
        { duration: opening ? 420 : 260, easing: 'cubic-bezier(.16,1,.3,1)' }
      );
      animation.onfinish = () => { if (!opening) details.open = false; animation = null; };
    });
  }

  // One scroll loop: header state, liquid tilt (spring back to level), reading fill, evidence window, soft parallax.
  let lastY = scrollY, tilt = 0, velocity = 0, target = 0, ticking = false, settleFrame, rings = null;
  const wide = matchMedia('(min-width: 750px)');
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  function frame() {
    ticking = false;
    const y = scrollY, vh = innerHeight;
    root.classList.toggle('ol-scrolled', y > 24);
    target = clamp((y - lastY) * -0.9, -14, 14);
    lastY = y;
    for (const { el, words } of fills) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      const progress = clamp((vh * 0.88 - rect.top) / (vh * 0.5 + rect.height), 0, 1);
      const lit = progress * (words.length + 4);
      words.forEach((word, i) => word.style.setProperty('--w', clamp(lit - i, 0, 1).toFixed(2)));
    }
    for (const el of openers) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      el.style.setProperty('--ol-open', clamp((vh - rect.top) / (vh * 0.55), 0, 1).toFixed(3));
    }
    if (wide.matches) for (const { el, target: node, rate } of parallax) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      const offset = (rect.top + rect.height / 2 - vh / 2) * rate;
      node.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0) scale(${1 + Math.abs(rate) * 2})`;
    }
    if (!settleFrame) settleFrame = requestAnimationFrame(settle);
  }

  function settle() {
    // Critically-damped-ish spring: the liquid leans against the scroll, overshoots slightly, then rests level.
    velocity += (target - tilt) * 0.12;
    velocity *= 0.78;
    tilt += velocity;
    target *= 0.82;
    const value = tilt.toFixed(2);
    if (!rings) rings = Array.from(document.querySelectorAll('.ol-rings, .ol-ring-glyph'));
    for (const ring of rings) ring.style.setProperty('--ol-tilt', value);
    settleFrame = Math.abs(tilt) > 0.02 || Math.abs(velocity) > 0.02 || Math.abs(target) > 0.02 ? requestAnimationFrame(settle) : null;
    if (!settleFrame) for (const ring of rings) ring.style.setProperty('--ol-tilt', '0');
  }

  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }, { passive: true });
  addEventListener('resize', () => requestAnimationFrame(frame), { passive: true });

  // On phones the rings also answer to the device itself, where the browser allows it without a prompt.
  if ('DeviceOrientationEvent' in window && typeof DeviceOrientationEvent.requestPermission !== 'function') {
    let lastGamma = 0, lastOrientation = 0;
    addEventListener('deviceorientation', event => {
      if (event.gamma == null) return;
      const now = performance.now();
      if (now - lastOrientation < 120 || Math.abs(event.gamma - lastGamma) < 1.5) return;
      lastOrientation = now; lastGamma = event.gamma;
      target = clamp(-event.gamma * 0.5, -14, 14);
      if (!settleFrame) settleFrame = requestAnimationFrame(settle);
    }, { passive: true });
  }

  scan();
  frame();
  document.addEventListener('shopify:section:load', event => { rings = null; scan(event.target); frame(); });
  // Failsafe: if anything is still hidden a few seconds after load (observer edge cases), show it.
  addEventListener('load', () => setTimeout(() => {
    document.querySelectorAll('.ol-r:not(.ol-in), .ol-h:not(.ol-in)').forEach(el => {
      const rect = el.getBoundingClientRect();
      if (rect.top < innerHeight && rect.bottom > 0) el.classList.add('ol-in');
    });
  }, 2500));
})();
